import { create } from "zustand";

/**
 * Реестр обработчиков «потяни-чтобы-обновить».
 *
 * Жест смонтирован ОДИН раз в оболочке (`components/layout/app-pull-to-refresh`),
 * а не оборачивает каждый список руками. Раньше было наоборот: 23 экрана несли
 * обёртку вокруг своего куска разметки, а остальные ~70 — ничего, и «обновить»
 * существовало пятнами. Разница видна не по коду, а по ощущению: жест либо есть
 * везде, либо его нет.
 *
 * Экран РЕГИСТРИРУЕТ обработчик только когда обновление у него нетиповое
 * (пересборка индекса, ре-синк, перезагрузка страницы). Всё остальное закрывает
 * обработчик по умолчанию оболочки — перезапрос активных запросов TanStack Query
 * и инвалидация загрузчиков роутера, а это ровно то, что делали ручные
 * `query.refetch()` из обёрток, только не выборочно.
 *
 * Стек, а не одно значение: вложенные поверхности (экран + панель внутри него)
 * монтируются и размонтируются в произвольном порядке, и «последний
 * зарегистрированный» обязан пережить размонтирование любого другого.
 */
interface RegisteredHandler {
	id: number;
	run: () => Promise<unknown> | unknown;
}

interface PullToRefreshState {
	handlers: RegisteredHandler[];
	register: (id: number, run: () => Promise<unknown> | unknown) => void;
	unregister: (id: number) => void;
}

export const usePullToRefreshStore = create<PullToRefreshState>((set) => ({
	handlers: [],
	register: (id, run) =>
		set((s) => ({
			handlers: [...s.handlers.filter((h) => h.id !== id), { id, run }],
		})),
	unregister: (id) => set((s) => ({ handlers: s.handlers.filter((h) => h.id !== id) })),
}));

/** Обработчик, который должен отработать по жесту: последний зарегистрированный. */
export function selectActiveHandler(
	s: PullToRefreshState,
): (() => Promise<unknown> | unknown) | null {
	return s.handlers.at(-1)?.run ?? null;
}
